<template>
  <panel ref="panel" :title="`${$tc('tournament.registered_officials')} (${officials.length})`" collapsable>
    <template v-slot:actions>
      <el-button
        v-if="canEdit"
        type="primary"
        icon="el-icon-plus"
        size="medium"
        round
        @click="$refs.panel.expanded = true; openManageOfficial = true">
        {{ $tc('tournament.register_official') }}
      </el-button>

      <el-tooltip v-if="officials.length" :content="$tc('general.print')" placement="top">
        <el-button
          icon="el-icon-printer"
          size="medium"
          circle
          @click="openPrint($tc('club.official', 2), $refs.printTable ? $refs.printTable.$el : null)" />
      </el-tooltip>
    </template>

    <officials-table
      :data="officials"
      :can-edit="canEdit"
      :show-club-name="showClubName"
      custom-logic
      hide-edit
      @edit="openEdit($event)"
      @custom-delete="onRemove($event)" />

    <print-officials-table ref="printTable" v-show="false" :data="officials" :show-club-name="showClubName" :tournament="tournament" />

    <main-drawer v-if="openManageOfficial" :title="$tc('tournament.registered_officials')" @close="onClose()">
      <main-select v-model="form.officialId" :label="$tc('club.official')" :options="officialOptions" :validator="$v.form.officialId" />

      <template v-slot:footer>
        <div class="flex-buttons">
          <el-button @click="onClose()">{{ $tc('general.cancel') }}</el-button>
          <el-button type="primary" @click="addOfficial()">{{ $tc('tournament.register_official') }}</el-button>
        </div>
      </template>
    </main-drawer>
  </panel>
</template>

<script>
import openPrint from '@/common/print'
import { required } from 'vuelidate/lib/validators'
import errorHandler from '@/common/errorHandler.js'

import OfficialsTable from './OfficialsTable'
import PrintOfficialsTable from './PrintOfficialsTable'

export default {
  components: {
    OfficialsTable,
    PrintOfficialsTable
  },

  props: {
    club: {
      type: Object,
      default: null
    },

    tournament: {
      type: Object,
      required: true
    },

    showClubName: {
      type: Boolean,
      default: false
    },

    canEdit: {
      type: Boolean,
      default: false
    },

    officials: {
      type: Array,
      default: null
    }
  },

  data () {
    return {
      clubId: null,
      form: {
        officialId: null
      },
      openPrint,
      openManageOfficial: false
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    },

    officialOptions () {
      const officials = this.club?.officials || this.user.club?.officials || []
      const filteredOfficials = officials.filter(official => official.isActive)

      return filteredOfficials.map(official => ({
        text: official.firstName + ' ' + official.lastName,
        id: official.id,
        disabled: this.officials.some(o => o.id === official.id)
      }))
    }
  },

  methods: {
    async addOfficial () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return
      this.loading = true

      try {
        const tournament = await this.$store.dispatch('tournaments/registerOfficial', {
          id: this.tournament?.id,
          officialId: this.form.officialId
        })
        this.updateActiveTournament(tournament)
        this.$message.success(this.$tc('messages.success_registered_official'))
        this.onClose()
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    async onRemove (official) {
      try {
        const tournament = await this.$store.dispatch('tournaments/removeOfficial', {
          id: this.tournament?.id,
          officialId: official.id
        })
        this.updateActiveTournament(tournament)
        this.$message.success(this.$tc('messages.success_removed_official'))
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    updateActiveTournament (tournament) {
      if (tournament.id === this.tournament?.id) {
        this.$store.commit('app/setActiveTournament', tournament)
      }
    },

    onClose () {
      this.form.officialId = null
      this.$v.$reset()
      this.openManageOfficial = false
    }
  },

  validations: {
    form: {
      officialId: { required }
    }
  }
}
</script>
